import type { GameSnapshot, SnapshotEntity } from "./types";

export type GameplayUiModel = {
  instructionVisible?: boolean;
  instructionText?: string;
  statusText?: string;
  labels?: {
    leaderboard?: string;
    hp?: string;
    level?: string;
    xp?: string;
    defeatTitle?: string;
    defeatSubtitle?: string;
  };
};

type UiLayout = {
  scale: number;
  compact: boolean;
  portrait: boolean;
  margin: number;
};

export function renderGameplayUi(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  snapshot: GameSnapshot,
  ui: GameplayUiModel
): void {
  const layout = createUiLayout(width, height);

  renderLeaderboard(context, width, height, snapshot, ui, layout);
  renderBottomLeftPanel(context, width, height, snapshot, ui, layout);

  if (ui.instructionVisible && ui.instructionText) {
    renderCenterHint(context, width, height, ui.instructionText, layout);
  }

  if (ui.statusText) {
    renderStatus(context, width, ui.statusText, layout);
  }

  if (snapshot.defeated) {
    renderDefeatDim(context, width, height);
  } else if (getPendingUpgrades(snapshot).length > 0) {
    renderUpgradeDim(context, width, height);
  }
}

function renderLeaderboard(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  snapshot: GameSnapshot,
  ui: GameplayUiModel,
  layout: UiLayout
): void {
  const leaderboard = snapshot.leaderboard ?? [];

  if (leaderboard.length === 0) {
    return;
  }

  const title = ui.labels?.leaderboard ?? "Leaderboard";
  const levelLabel = layout.compact ? "Lv" : ui.labels?.level ?? "Level";
  const panelWidth = layout.compact
    ? clamp(width * 0.46, 156, Math.min(220, width - layout.margin * 2))
    : clamp(width * 0.32, 240, 310);
  const x = width - panelWidth - layout.margin;
  const y = layout.margin;
  const rowHeight = Math.round(clamp(25 * layout.scale, 17, 25));
  const headerHeight = Math.round(clamp(42 * layout.scale, 28, 42));
  const maxPanelHeight = height * (layout.compact ? (layout.portrait ? 0.52 : 0.36) : 0.46);
  const maxRowsByHeight = Math.max(3, Math.floor((maxPanelHeight - headerHeight) / rowHeight));
  const compactMaxRows = layout.portrait ? 8 : 5;
  const visibleCount = Math.min(
    leaderboard.length,
    layout.compact ? Math.min(compactMaxRows, maxRowsByHeight) : maxRowsByHeight
  );
  const hiddenCount = Math.max(0, leaderboard.length - visibleCount);
  const footerHeight = hiddenCount > 0 ? rowHeight : 0;
  const panelHeight = headerHeight + visibleCount * rowHeight + footerHeight;
  const radius = Math.round(clamp(14 * layout.scale, 9, 14));
  const paddingX = Math.round(clamp(14 * layout.scale, 8, 14));
  const titleFontSize = Math.round(clamp(16 * layout.scale, 11, 16));
  const rankFontSize = Math.round(clamp(14 * layout.scale, 10, 14));
  const nameFontSize = Math.round(clamp(14 * layout.scale, 10, 14));
  const levelFontSize = Math.round(clamp(13 * layout.scale, 9, 13));

  context.save();
  roundedRect(context, x, y, panelWidth, panelHeight, radius);
  context.fillStyle = "rgba(255,255,255,0.6)";
  context.fill();
  context.strokeStyle = "rgba(0,0,0,0.1)";
  context.stroke();

  context.fillStyle = "#111111";
  context.font = `800 ${titleFontSize}px system-ui, sans-serif`;
  context.textAlign = "left";
  context.textBaseline = "middle";
  context.fillText(title, x + paddingX, y + headerHeight * 0.48, panelWidth - paddingX * 2);

  for (let index = 0; index < visibleCount; index += 1) {
    const entry = leaderboard[index];

    if (!entry) {
      continue;
    }

    const rowY = y + headerHeight + rowHeight * index + rowHeight * 0.5;
    const rankText = `${index + 1}.`;
    const crownText = entry.is_leader ?? entry.isLeader ? "♛" : "";
    const levelText = `${levelLabel} ${entry.level}`;
    const nameText = entry.name;

    context.fillStyle = index === 0 ? "#111111" : "#333333";
    context.font = `800 ${rankFontSize}px system-ui, sans-serif`;
    context.fillText(rankText, x + paddingX, rowY);

    let nameX = x + paddingX + Math.round(clamp(26 * layout.scale, 20, 26));

    if (crownText) {
      context.fillText(crownText, nameX, rowY);
      nameX += Math.round(clamp(18 * layout.scale, 12, 18));
    }

    context.font = `800 ${levelFontSize}px system-ui, sans-serif`;
    const levelWidth = context.measureText(levelText).width;
    const levelX = x + panelWidth - paddingX;
    const nameMaxWidth = Math.max(24, levelX - nameX - levelWidth - Math.round(10 * layout.scale));

    context.font = `750 ${nameFontSize}px system-ui, sans-serif`;
    drawFittedInlineText(context, nameText, nameX, rowY, nameMaxWidth, nameFontSize, 8);

    context.font = `800 ${levelFontSize}px system-ui, sans-serif`;
    context.fillStyle = "rgba(0,0,0,0.66)";
    context.textAlign = "right";
    context.fillText(levelText, levelX, rowY);
    context.textAlign = "left";
  }

  if (hiddenCount > 0) {
    const rowY = y + headerHeight + visibleCount * rowHeight + rowHeight * 0.5;
    context.font = `800 ${Math.round(clamp(12 * layout.scale, 9, 12))}px system-ui, sans-serif`;
    context.fillStyle = "rgba(0,0,0,0.58)";
    context.textAlign = "center";
    context.fillText(`+${hiddenCount}`, x + panelWidth * 0.5, rowY);
  }

  context.restore();
}

function renderBottomLeftPanel(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  snapshot: GameSnapshot,
  ui: GameplayUiModel,
  layout: UiLayout
): void {
  const local = findLocalEntity(snapshot);
  const panelWidth = layout.compact ? clamp(width * 0.5, 176, 230) : 252;
  const panelHeight = Math.round(clamp(94 * layout.scale, 62, 94));
  const x = layout.margin;
  const y = height - layout.margin - panelHeight;
  const radius = Math.round(clamp(14 * layout.scale, 9, 14));
  const paddingX = Math.round(clamp(14 * layout.scale, 9, 14));
  const hpLabel = ui.labels?.hp ?? "HP";
  const levelLabel = layout.compact ? "Lv" : ui.labels?.level ?? "Level";
  const xpLabel = ui.labels?.xp ?? "XP";
  const labelFontSize = Math.round(clamp(15 * layout.scale, 10, 15));
  const barHeight = Math.round(clamp(14 * layout.scale, 9, 14));

  context.save();
  roundedRect(context, x, y, panelWidth, panelHeight, radius);
  context.fillStyle = "rgba(255,255,255,0.6)";
  context.fill();
  context.strokeStyle = "rgba(0,0,0,0.1)";
  context.stroke();

  context.fillStyle = "#111111";
  context.font = `800 ${labelFontSize}px system-ui, sans-serif`;
  context.textAlign = "left";
  context.textBaseline = "middle";

  const hp = Math.round(local?.hp ?? 0);
  const maxHp = Math.round(local?.max_hp ?? local?.maxHp ?? 100);
  const level = local?.level ?? 1;
  const xp = local?.xp ?? 0;
  const xpProgress = getXpProgress(local, level, xp);
  const firstLineY = y + panelHeight * 0.24;
  const secondLineY = y + panelHeight * 0.5;
  const barY = y + panelHeight - paddingX - barHeight;

  context.fillText(`${hpLabel} ${hp}/${maxHp}`, x + paddingX, firstLineY, panelWidth - paddingX * 2);
  context.fillText(`${levelLabel} ${level}`, x + paddingX, secondLineY, panelWidth * 0.45);

  context.textAlign = "right";
  context.fillText(
    `${xpLabel} ${xpProgress.current}/${xpProgress.required}`,
    x + panelWidth - paddingX,
    secondLineY,
    panelWidth * 0.52
  );
  context.textAlign = "left";

  drawProgressBar(
    context,
    x + paddingX,
    barY,
    panelWidth - paddingX * 2,
    barHeight,
    xpProgress.current,
    xpProgress.required
  );

  context.restore();
}

function getXpProgress(
  entity: SnapshotEntity | null,
  level: number,
  totalXp: number
): { current: number; required: number } {
  const snapshotCurrent = entity?.xp_in_level ?? entity?.xpInLevel;
  const snapshotRequired = entity?.xp_to_next_level ?? entity?.xpToNextLevel;

  if (typeof snapshotCurrent === "number" && typeof snapshotRequired === "number") {
    return {
      current: Math.max(0, Math.floor(snapshotCurrent)),
      required: Math.max(1, Math.floor(snapshotRequired))
    };
  }

  const currentLevelStart = xpRequiredForLevel(level);
  const nextLevelStart = xpRequiredForLevel(level + 1);

  return {
    current: Math.max(0, Math.floor(totalXp - currentLevelStart)),
    required: Math.max(1, Math.floor(nextLevelStart - currentLevelStart))
  };
}

function xpRequiredForLevel(level: number): number {
  if (level <= 1) {
    return 0;
  }

  return Math.floor(5 + Math.pow(level, 1.55) * 4);
}

function drawProgressBar(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  value: number,
  max: number
): void {
  const progress = Math.max(0, Math.min(1, value / Math.max(1, max)));

  roundedRect(context, x, y, width, height, height * 0.5);
  context.fillStyle = "rgba(0,0,0,0.12)";
  context.fill();

  if (progress > 0) {
    roundedRect(context, x, y, width * progress, height, height * 0.5);
    context.fillStyle = "#7c9dff";
    context.fill();
  }

  context.strokeStyle = "rgba(0,0,0,0.14)";
  context.lineWidth = 1;
  roundedRect(context, x, y, width, height, height * 0.5);
  context.stroke();
}

function renderCenterHint(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  text: string,
  layout: UiLayout
): void {
  const fontSize = Math.round(clamp(22 * layout.scale, 13, 22));

  context.save();
  context.font = `800 ${fontSize}px system-ui, sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineWidth = Math.max(3, 5 * layout.scale);
  context.strokeStyle = "rgba(255,255,255,0.9)";
  context.fillStyle = "#222222";
  context.strokeText(text, width * 0.5, height * 0.72, width * 0.88);
  context.fillText(text, width * 0.5, height * 0.72, width * 0.88);
  context.restore();
}

function renderStatus(
  context: CanvasRenderingContext2D,
  width: number,
  text: string,
  layout: UiLayout
): void {
  const fontSize = Math.round(clamp(15 * layout.scale, 10, 15));

  context.save();
  context.font = `700 ${fontSize}px system-ui, sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = "rgba(0,0,0,0.55)";
  context.fillText(text, width * 0.5, layout.margin + fontSize, width * 0.9);
  context.restore();
}

function renderUpgradeDim(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  context.save();
  context.fillStyle = "rgba(0,0,0,0.28)";
  context.fillRect(0, 0, width, height);
  context.restore();
}

function renderDefeatDim(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  context.save();
  context.fillStyle = "rgba(0,0,0,0.44)";
  context.fillRect(0, 0, width, height);
  context.restore();
}

function getPendingUpgrades(snapshot: GameSnapshot): unknown[] {
  return snapshot.pending_upgrades ?? snapshot.pendingUpgrades ?? [];
}

function findLocalEntity(snapshot: GameSnapshot): SnapshotEntity | null {
  const localId = extractId(snapshot.local_player_id ?? snapshot.localPlayerId);

  if (localId === null) {
    return (
      snapshot.entities.find((entity) => {
        const kind = String(entity.kind).toLowerCase();
        return kind === "player" || kind === "bot";
      }) ?? null
    );
  }

  return (
    snapshot.entities.find((entity) => {
      const owner = extractId(entity.owner);
      return owner === localId;
    }) ?? null
  );
}

function extractId(value: unknown): number | null {
  if (typeof value === "number") {
    return value;
  }

  if (value && typeof value === "object" && "0" in value) {
    const id = (value as { 0?: unknown })[0];
    return typeof id === "number" ? id : null;
  }

  return null;
}

function drawFittedInlineText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  baseSize: number,
  minSize: number
): void {
  let size = baseSize;

  while (size > minSize) {
    context.font = `750 ${size}px system-ui, sans-serif`;

    if (context.measureText(text).width <= maxWidth) {
      break;
    }

    size -= 1;
  }

  context.font = `750 ${size}px system-ui, sans-serif`;
  context.fillText(text, x, y, maxWidth);
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

function createUiLayout(width: number, height: number): UiLayout {
  const compact = width <= 640 || height <= 520;
  const portrait = height > width;
  const scale = clamp(Math.min(width / 1024, height / 720), compact ? 0.58 : 0.76, 1);
  const margin = Math.round(clamp(18 * scale, compact ? 8 : 12, 18));

  return {
    scale,
    compact,
    portrait,
    margin
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}